/**
 * AdminCommissions — monthly sales-rep commission ledger. Shows what each rep
 * is owed for a calendar month (their rate applied to RinsePay's revenue from
 * the owners they referred: collected $100/mo platform fees + net 1% app
 * fees), with a per-owner drill-down and Mark Paid (snapshot) / Unmark.
 * Rates themselves are edited on the Referrals screen; per-owner overrides on
 * the owner detail page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, Modal, TextInput } from 'react-native';
import { Redirect, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import { useAdminPermissions } from '../../src/hooks/useAdminPermissions';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import Button from '../../src/components/Button';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

const MOBILE_BREAKPOINT = 768;

interface PaidInfo { amountCents: number; paidAt: string; paidByEmail: string | null; note: string | null }
interface RepRow {
  codeId: string; code: string; displayName: string;
  rateBps: number | null; durationMonths: number | null; isActive: boolean;
  ownerCount: number; computedCents: number; amountCents: number;
  paid: PaidInfo | null;
}
interface OwnerLine {
  ownerId: string; businessName: string; rateBps: number; overridden: boolean;
  platformFeeCents: number; appFeeCents: number; commissionCents: number;
  accrualStartsAt: string | null; windowEndsAt: string | null;
}
interface RepDetail {
  month: string;
  code: { id: string; code: string; displayName: string; rateBps: number | null; durationMonths: number | null; commissionStartsAt: string | null; isActive: boolean };
  computed: { totalCents: number; owners: OwnerLine[] };
  paid: (PaidInfo & { breakdown: OwnerLine[] | null }) | null;
}

const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const fmtRate = (bps: number) => `${(bps / 100).toFixed(2).replace(/\.?0+$/, '')}%`;
const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** 'YYYY-MM' → 'August 2026' (parse as UTC noon to dodge tz off-by-one). */
const fmtMonth = (m: string) => new Date(`${m}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const shiftMonth = (m: string, delta: number): string => {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(Date.UTC(y, mo - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const currentMonth = (): string => {
  const now = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' }); // YYYY-MM-DD
  return now.slice(0, 7);
};

export default function AdminCommissions() {
  const { has, loaded } = useAdminPermissions();
  if (loaded && !has('platform_referrals')) return <Redirect href="/admin/users" />;
  const canWrite = has('platform_referrals', 'write');
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < MOBILE_BREAKPOINT;
  const styles = useStyles(colors, isMobile);
  const toast = useToast();
  const router = useRouter();

  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState<RepRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalCents, setTotalCents] = useState(0);
  const [unpaidCents, setUnpaidCents] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Drill-down
  const [detail, setDetail] = useState<RepDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Mark-paid modal
  const [payTarget, setPayTarget] = useState<RepRow | null>(null);
  const [payNote, setPayNote] = useState('');
  const [paySaving, setPaySaving] = useState(false);

  const fetchRows = useCallback(async (m: string, p = 1, s = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month: m, page: String(p), limit: '10' });
      if (s) params.set('search', s);
      const res = await api<{ rows: RepRow[]; total: number; totalCents: number; unpaidCents: number }>(`/api/admin/commissions?${params}`);
      setRows(res.rows);
      setTotal(res.total);
      setTotalCents(res.totalCents);
      setUnpaidCents(res.unpaidCents);
      setPage(p);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchDetail = useCallback(async (codeId: string, m: string) => {
    setDetailLoading(true);
    try {
      const res = await api<RepDetail>(`/api/admin/commissions/${codeId}?month=${m}`);
      setDetail(res);
    } catch (err) { console.error(err); }
    finally { setDetailLoading(false); }
  }, []);

  useEffect(() => { setDetail(null); fetchRows(month, 1, search); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const changeMonth = (delta: number) => {
    const next = shiftMonth(month, delta);
    if (next > currentMonth()) return; // future months have no revenue yet
    setMonth(next);
  };

  const handleMarkPaid = async () => {
    if (!payTarget) return;
    setPaySaving(true);
    try {
      await api(`/api/admin/commissions/${payTarget.codeId}/mark-paid`, {
        method: 'POST',
        body: { month, note: payNote.trim() || undefined },
      });
      toast.show({ type: 'success', title: `Marked ${payTarget.code} paid for ${fmtMonth(month)}` });
      setPayTarget(null); setPayNote('');
      fetchRows(month, page, search);
      if (detail?.code.id === payTarget.codeId) fetchDetail(payTarget.codeId, month);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Mark paid failed', message: err.message });
    } finally { setPaySaving(false); }
  };

  const handleUnmark = async (row: RepRow) => {
    try {
      await api(`/api/admin/commissions/${row.codeId}/mark-paid?month=${month}`, { method: 'DELETE' });
      toast.show({ type: 'success', title: `${row.code} back to unpaid for ${fmtMonth(month)}` });
      fetchRows(month, page, search);
      if (detail?.code.id === row.codeId) fetchDetail(row.codeId, month);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Unmark failed', message: err.message });
    }
  };

  if (loading && rows.length === 0 && !detail) {
    return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  }

  const isCurrentMonth = month === currentMonth();
  const detailLines: OwnerLine[] = detail
    ? (detail.paid?.breakdown ?? detail.computed.owners)
    : [];

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer role="admin">

        <View style={styles.headerRow}>
          <Text style={styles.pageTitle}>Commissions</Text>
          <View style={styles.monthNav}>
            <TouchableOpacity style={styles.monthBtn} onPress={() => changeMonth(-1)} accessibilityLabel="Previous month">
              <Ionicons name="chevron-back" size={18} color={colors.primary} />
            </TouchableOpacity>
            <Text style={styles.monthLabel}>{fmtMonth(month)}</Text>
            <TouchableOpacity
              style={[styles.monthBtn, isCurrentMonth && { opacity: 0.3 }]}
              onPress={() => changeMonth(1)}
              disabled={isCurrentMonth}
              accessibilityLabel="Next month"
            >
              <Ionicons name="chevron-forward" size={18} color={colors.primary} />
            </TouchableOpacity>
          </View>
        </View>
        <Text style={styles.pageSub}>
          What each rep is owed for {fmtMonth(month)}: their rate on the $100/mo platform fees plus RinsePay's 1% transaction fees from customers they brought in.
          Unpaid months recalculate live; Mark Paid freezes the amount. Set rates on the Referrals page.
        </Text>

        {/* Month summary */}
        <View style={styles.summaryRow}>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryValue}>{fmtMoney(totalCents)}</Text>
            <Text style={styles.summaryLabel}>total {fmtMonth(month)}</Text>
          </View>
          <View style={styles.summaryCard}>
            <Text style={[styles.summaryValue, unpaidCents > 0 && { color: colors.warning }]}>{fmtMoney(unpaidCents)}</Text>
            <Text style={styles.summaryLabel}>unpaid</Text>
          </View>
        </View>

        {/* Reps list */}
        <View style={styles.card}>
          <AdminToolbar
            search={search}
            onSearchChange={setSearch}
            onSearch={() => { setDetail(null); fetchRows(month, 1, search); }}
            total={total}
            page={page}
            limit={10}
            onPageChange={(p) => fetchRows(month, p, search)}
            csvEndpoint={`/api/admin/commissions?${new URLSearchParams({ month, ...(search && { search }) })}`}
            csvFilename={`commissions-${month}.csv`}
            label="rep"
          />
          {loading ? (
            <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
          ) : rows.length === 0 ? (
            <Text style={styles.empty}>No reps with commission rates yet. Set a rate on a rep code in Referrals.</Text>
          ) : (
            rows.map(r => (
              <TouchableOpacity
                key={r.codeId}
                style={[styles.listRow, detail?.code.id === r.codeId && styles.listRowSelected]}
                onPress={() => fetchDetail(r.codeId, month)}
              >
                <View style={styles.listBody}>
                  <View style={styles.codeLine}>
                    <Text style={styles.codeText}>{r.code}</Text>
                    {r.paid ? (
                      <View style={[styles.badge, { backgroundColor: colors.success + '18' }]}>
                        <Text style={[styles.badgeText, { color: colors.success }]}>Paid {fmtDate(r.paid.paidAt)}</Text>
                      </View>
                    ) : r.amountCents > 0 ? (
                      <View style={[styles.badge, { backgroundColor: colors.warning + '18' }]}>
                        <Text style={[styles.badgeText, { color: colors.warning }]}>Unpaid</Text>
                      </View>
                    ) : null}
                    {!r.isActive && (
                      <View style={[styles.badge, { backgroundColor: colors.danger + '15' }]}>
                        <Text style={[styles.badgeText, { color: colors.danger }]}>Inactive</Text>
                      </View>
                    )}
                  </View>
                  <Text style={styles.listSub} numberOfLines={1}>{r.displayName}</Text>
                  <Text style={styles.listMeta} numberOfLines={1}>
                    {r.rateBps != null ? `${fmtRate(r.rateBps)} · ${r.durationMonths != null ? `${r.durationMonths} mo` : 'forever'}` : 'per-owner overrides only'} · {r.ownerCount} referred owner{r.ownerCount === 1 ? '' : 's'}
                  </Text>
                  {r.paid?.note ? <Text style={styles.listMeta} numberOfLines={1}>{r.paid.note}</Text> : null}
                </View>
                <View style={styles.listRight}>
                  <Text style={[styles.amount, r.paid ? { color: colors.success } : r.amountCents > 0 ? { color: colors.text } : { color: colors.textLight }]}>
                    {fmtMoney(r.amountCents)}
                  </Text>
                  {canWrite && (r.paid ? (
                    <TouchableOpacity onPress={(e) => { e.stopPropagation?.(); handleUnmark(r); }}>
                      <Text style={styles.unmarkText}>unmark</Text>
                    </TouchableOpacity>
                  ) : r.amountCents > 0 ? (
                    <Button
                      title="Mark Paid"
                      size="sm"
                      variant="outline"
                      onPress={() => { setPayNote(''); setPayTarget(r); }}
                    />
                  ) : null)}
                </View>
              </TouchableOpacity>
            ))
          )}
        </View>

        {/* Per-owner drill-down */}
        {(detail || detailLoading) && (
          <>
            <View style={styles.headerRow}>
              <Text style={styles.sectionTitle}>
                {detail ? `${detail.code.displayName} (${detail.code.code}) — ${fmtMonth(month)}${detail.paid ? ' · paid snapshot' : ''}` : 'Loading…'}
              </Text>
              <TouchableOpacity onPress={() => setDetail(null)}>
                <Ionicons name="close-circle" size={22} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <View style={styles.card}>
              {detailLoading ? (
                <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
              ) : detailLines.length === 0 ? (
                <Text style={styles.empty}>No commissionable owners for this month</Text>
              ) : (
                <>
                  {!isMobile && (
                    <View style={[styles.breakdownRow, styles.breakdownHeader]}>
                      <Text style={[styles.breakdownName, styles.headerText]}>Customer</Text>
                      <Text style={[styles.breakdownCell, styles.headerText]}>Rate</Text>
                      <Text style={[styles.breakdownCell, styles.headerText]}>Platform fee</Text>
                      <Text style={[styles.breakdownCell, styles.headerText]}>1% app fees</Text>
                      <Text style={[styles.breakdownCell, styles.headerText]}>Commission</Text>
                    </View>
                  )}
                  {detailLines.map(o => (
                    <TouchableOpacity key={o.ownerId} style={styles.breakdownRow} onPress={() => router.push(`/admin/owner/${o.ownerId}` as never)}>
                      <View style={styles.breakdownName}>
                        <Text style={styles.listName} numberOfLines={1}>{o.businessName}</Text>
                        {isMobile && (
                          <Text style={styles.listMeta}>
                            {fmtRate(o.rateBps)}{o.overridden ? ' (override)' : ''} · fee {fmtMoney(o.platformFeeCents)} · app {fmtMoney(o.appFeeCents)}
                          </Text>
                        )}
                        {!isMobile && o.overridden && <Text style={styles.listMeta}>override rate</Text>}
                        {o.windowEndsAt && <Text style={styles.listMeta}>ends {fmtDate(o.windowEndsAt)}</Text>}
                      </View>
                      {!isMobile && (
                        <>
                          <Text style={styles.breakdownCell}>{fmtRate(o.rateBps)}</Text>
                          <Text style={styles.breakdownCell}>{fmtMoney(o.platformFeeCents)}</Text>
                          <Text style={styles.breakdownCell}>{fmtMoney(o.appFeeCents)}</Text>
                        </>
                      )}
                      <Text style={[styles.breakdownCell, styles.amount]}>{fmtMoney(o.commissionCents)}</Text>
                    </TouchableOpacity>
                  ))}
                  <View style={[styles.breakdownRow, { borderBottomWidth: 0 }]}>
                    <Text style={[styles.breakdownName, styles.listName]}>Total</Text>
                    <Text style={[styles.breakdownCell, styles.amount]}>
                      {fmtMoney(detail?.paid ? detail.paid.amountCents : detail?.computed.totalCents ?? 0)}
                    </Text>
                  </View>
                </>
              )}
            </View>
          </>
        )}

        {/* Mark-paid confirm modal */}
        <Modal visible={!!payTarget} transparent animationType="fade" onRequestClose={() => setPayTarget(null)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalCard}>
              {payTarget && (
                <>
                  <Text style={styles.modalTitle}>Mark {payTarget.code} paid</Text>
                  <Text style={styles.modalBody}>
                    {fmtMonth(month)} commission for {payTarget.displayName}: <Text style={{ fontWeight: fontWeight.bold }}>{fmtMoney(payTarget.amountCents)}</Text>.
                    {'\n'}This freezes the amount and breakdown for this month. Pay the rep outside RinsePay (check, Zelle, etc.), then record it here.
                    {isCurrentMonth ? '\n\nHeads up: this month is still in progress, so revenue may still come in after you freeze it.' : ''}
                  </Text>
                  <Text style={styles.fieldLabel}>Note (check #, Zelle ref, optional)</Text>
                  <TextInput
                    style={styles.input}
                    value={payNote}
                    onChangeText={setPayNote}
                    placeholder="Optional"
                    placeholderTextColor={colors.textLight}
                    maxLength={200}
                  />
                  <View style={styles.modalActions}>
                    <Button title="Cancel" variant="outline" size="sm" onPress={() => setPayTarget(null)} style={{ flex: 1 }} />
                    <Button title="Mark Paid" size="sm" onPress={handleMarkPaid} loading={paySaving} style={{ flex: 1 }} />
                  </View>
                </>
              )}
            </View>
          </View>
        </Modal>

      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: isMobile ? spacing.md : spacing.lg },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs, gap: spacing.sm },
  pageTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  pageSub: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.md, lineHeight: 20 },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text, flex: 1 },
  empty: { textAlign: 'center', color: c.textSecondary, paddingVertical: spacing.md, fontSize: fontSize.sm },

  monthNav: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  monthBtn: { padding: spacing.xs },
  monthLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, minWidth: 110, textAlign: 'center' },

  summaryRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md },
  summaryCard: {
    flex: 1, backgroundColor: c.surface, borderRadius: borderRadius.lg,
    padding: spacing.md, alignItems: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1,
  },
  summaryValue: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, fontVariant: ['tabular-nums'] },
  summaryLabel: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },

  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.lg,
    padding: spacing.md, marginBottom: spacing.md,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1,
  },

  listRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: spacing.sm,
  },
  listRowSelected: { backgroundColor: c.primaryBg, borderRadius: borderRadius.sm },
  listBody: { flex: 1, minWidth: 0 },
  codeLine: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  codeText: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.text },
  badge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: borderRadius.sm },
  badgeText: { fontSize: 10, fontWeight: fontWeight.semibold },
  listName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  listSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  listMeta: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  listRight: { alignItems: 'flex-end', gap: 4 },
  amount: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.text, fontVariant: ['tabular-nums'] },
  unmarkText: { fontSize: fontSize.xs, color: c.danger, textDecorationLine: 'underline' },

  breakdownRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: spacing.sm,
  },
  breakdownHeader: { paddingVertical: 4 },
  breakdownName: { flex: 1, minWidth: 0 },
  breakdownCell: { minWidth: 76, textAlign: 'right', fontSize: fontSize.sm, color: c.text, fontVariant: ['tabular-nums'] },
  headerText: { fontSize: 10, color: c.textLight, fontWeight: fontWeight.semibold, textTransform: 'uppercase' },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  modalCard: { backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: 420 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  modalBody: { fontSize: fontSize.sm, color: c.textSecondary, lineHeight: 20 },
  fieldLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.textSecondary, marginBottom: 4, marginTop: spacing.sm },
  input: {
    borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm, paddingVertical: 8, fontSize: fontSize.sm, color: c.text, backgroundColor: c.background,
  },
  modalActions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.lg },
});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
